// Página PÚBLICA de verificación de contratos firmados (QR del sello).
// Ruta /verificar-contrato/:codigo — fuera del login (patrón /tv/:token).
// Solo muestra validez y datos mínimos (RPC autenticacion.verificar_contrato).
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Loader2, ShieldQuestion, XCircle } from 'lucide-react';
import { apiClient } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

interface Verificacion {
  valido: boolean;
  contrato?: string; tipo?: string; estado_contrato?: string;
  fecha_inicio?: string; firmado_at?: string;
  empresa?: string; empresa_ruc?: string; empleado?: string; cedula?: string;
}

export function VerificarContrato() {
  const { codigo } = useParams<{ codigo: string }>();
  const { data, isFetching, isError } = useQuery<Verificacion>({
    queryKey: ['verificar-contrato', codigo],
    queryFn: async () => (await apiClient.post<Verificacion>('/rpc/verificar_contrato', { _codigo: codigo })).data,
    enabled: !!codigo,
    retry: 1,
  });

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-card border rounded-xl shadow-sm p-8 space-y-5">
        <div className="text-center space-y-1">
          <h1 className="text-lg font-semibold">Verificación de contrato</h1>
          <p className="text-xs text-muted-foreground">Documento firmado electrónicamente · Plataforma ERP</p>
        </div>

        {isFetching && <div className="py-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" /></div>}

        {isError && (
          <div className="text-center space-y-2 py-4">
            <ShieldQuestion className="w-12 h-12 mx-auto text-pendiente" />
            <p className="text-sm text-muted-foreground">No se pudo consultar la verificación. Intenta de nuevo.</p>
          </div>
        )}

        {data && data.valido && (
          <>
            <div className="text-center space-y-1">
              <CheckCircle2 className="w-14 h-14 mx-auto text-estable" />
              <div className="text-xl font-bold text-estable">DOCUMENTO VÁLIDO</div>
              <p className="text-xs text-muted-foreground">Contrato firmado electrónicamente con certificado digital</p>
            </div>
            <div className="text-sm space-y-1.5 border-t pt-4">
              {[['Contrato', data.contrato], ['Empresa', `${data.empresa} (RUC ${data.empresa_ruc})`],
                ['Trabajador', data.empleado], ['Cédula', data.cedula],
                ['Tipo', data.tipo], ['Inicio de labores', data.fecha_inicio?.slice(0, 10)],
                ['Estado del contrato', data.estado_contrato],
                ['Firmado el', data.firmado_at ? new Date(data.firmado_at).toLocaleString(LOCALE, { hour12: false }) : '—'],
              ].map(([k, v]) => (
                <div key={k as string} className="flex justify-between gap-4">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="text-right font-medium">{v}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground border-t pt-3">
              La validez criptográfica completa de la firma puede comprobarse abriendo el PDF en Adobe Reader
              o en el validador oficial FirmaEC.
            </p>
          </>
        )}

        {data && !data.valido && (
          <div className="text-center space-y-2 py-4">
            <XCircle className="w-14 h-14 mx-auto text-urgente" />
            <div className="text-xl font-bold text-urgente">CÓDIGO NO VÁLIDO</div>
            <p className="text-sm text-muted-foreground">
              El código <span className="font-mono">{codigo}</span> no corresponde a ningún contrato firmado.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
